import { graphql } from "@repo/graphql-types/gql";
import { createFileRoute, redirect, useRouter, useSearch } from "@tanstack/react-router";
import HelpCircle from "@repo/icons/default/HelpCircle";
import Left from "@repo/icons/default/Left";
import type { DocumentType } from "@repo/graphql-types";
import { queryFn, useGraphQL } from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { FollowUpAppointmentReminderForm } from "@/components/assist/follow-up-appointment-reminder/follow-up-appointment-reminder-form";
import { FollowUpAppointmentReminderMessageDrawer } from "@/components/assist/follow-up-appointment-reminder/follow-up-appointment-reminder-message-drawer";
import { AppointmentChanel } from "@/lib/enums/appointment-chanel";
import { AppointmentTypeCategoryEnum } from "@/lib/enums/appointment-type-category-enum";
import { formatWithZonedDate, getCurrentDate } from "@/lib/helpers/date";

import type { JSX } from "react";

// biome-ignore lint/plugin/route-file-exports: predates the rule; move it out
export const FollowUpAppointmentReminderPatientAppointmentQuery = graphql(/* GraphQL */ `
  query FollowUpAppointmentReminderPatientAppointmentQuery($patientId: Int!, $today: datetime2!) {
    patientAppointments: pacientes(where: { codPaciente: { _eq: $patientId } } ) {
      Agendamentos(
        where: {
          codFinalidadeAgendamento: { _in: [1, 3] }
          _not: {
            _and: [
              { codAgendamentoOrigem: { _is_null: false } }
              { cancelado: { _eq: true } }
            ]
          }
        }
        order_by: { codAgendamento: desc }
        limit: 1
      ) {
        ...FollowUpAppointmentReminderMessageDrawerFragment

        codAgendamento
        codPaciente
        nome
        Usuario {
          apelido
          nome
          sobrenome
        }
        UsuarioCompromisso {
          nome
        }
        reminders: lembretes(
          where: { ativo: { _eq: true }, dataEnviado: { _is_null: true }, dataParaEnvio: { _gte: $today } }
          order_by: { dataParaEnvio: asc }
        ) {
          codAgendamento
        }
      }
    }
  }
`);

const FollowUpAppointmentReminderPatientQuery = graphql(/* GraphQL */ `
  query FollowUpAppointmentReminderPatientQuery(
    $codAgendamento: Int!
    $canalAgendamentoPaciente: Int!
    $categoriaPeriodoGarantido: Int!
    $dataInicio: date!
  ) {
    ...FollowUpAppointmentReminderFormFragment
  }
`);

const FollowUpAppointmentReminderPatientPage = (): JSX.Element => {
  const { appointment } = Route.useRouteContext();

  const searchParams: Record<string, unknown> = useSearch({ strict: false });

  const router = useRouter();

  const queryResult = useGraphQL(FollowUpAppointmentReminderPatientQuery, {
    codAgendamento: Number(appointment?.codAgendamento ?? 0),
    canalAgendamentoPaciente: AppointmentChanel.Paciente.valueOf(),
    categoriaPeriodoGarantido: AppointmentTypeCategoryEnum.PeriodoGarantido.valueOf(),
    dataInicio: formatWithZonedDate(new Date(), "yyyy-MM-dd"),
  });

  const { data } = queryResult;

  const openAboutDrawer = (): void => {
    void router.navigate({
      to: ".",
      search: {
        ...searchParams,
        action: "about",
      },
    });
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Lembrete de retorno" align="center" />
        <HeaderButton icon={HelpCircle} align="end" onClick={openAboutDrawer} />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? <FollowUpAppointmentReminderForm data={data} /> : null}
          {appointment ? (
            <FollowUpAppointmentReminderMessageDrawer data={appointment} />
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute(
  "/waiting-room/follow-up-appointment-reminder/patient/$patientId",
)({
  component: FollowUpAppointmentReminderPatientPage,
  beforeLoad: async (opts) => {
    const { action } = opts.location.search as { action?: string };
    const { patientId } = opts.params;
    const patientIdNumber = Number(patientId);
    const today = getCurrentDate().toISOString();

    const patientData = await opts.context.queryClient.ensureQueryData<
      DocumentType<typeof FollowUpAppointmentReminderPatientAppointmentQuery>
    >({
      queryKey: [
        "FollowUpAppointmentReminderPatientAppointmentQuery",
        { patientId: patientIdNumber, today },
      ],
      queryFn: () =>
        queryFn(
          FollowUpAppointmentReminderPatientAppointmentQuery,
          opts.context.authClient,
          {
            patientId: patientIdNumber,
            today,
          },
        ),
    });

    const patientAppointments = patientData.patientAppointments.find(
      (appointment) => appointment.Agendamentos[0]?.codPaciente === patientIdNumber,
    );

    const patientLastAppointment = patientAppointments?.Agendamentos[0];

    const patientLastAppointmentId = patientLastAppointment?.codAgendamento;

    const hasReminder = patientLastAppointment?.reminders.some(
      (reminder) => reminder.codAgendamento === patientLastAppointmentId,
    );

    if (hasReminder && action !== "show-follow-up-appointment-reminder-drawer") {
      redirect({
        to: "/settings/assist/follow-up-appointment-reminder/patients",
        search: { action: "open-patient-with-reminder-drawer" },
        throw: true,
      });
    }

    return {
      appointment: patientLastAppointment,
    };
  },
});
